import {
  Col,
  Container,
  Row,
  Spinner,
  Table,
  Button,
  ToastContainer,
  Toast,
} from 'react-bootstrap';
import { useSupplierDocuments } from '../hooks/useSupplierDocuments';
import { SupplierDocumentUploader } from './SupplierDocumentUploader';
import { useDeleteDocument } from '../hooks/useDeleteDocument';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { useState } from 'react';
import { gToastPosition, gToastSentiment } from '../util/constst';

export function SupplierDocuments({
  supplierId,
  stackedOverModal = false,
}: {
  supplierId: string;
  stackedOverModal?: boolean;
}) {
  const { docs, loading, error, refetch } = useSupplierDocuments(supplierId);
  const { deleteDocument, deleting, error: deleteError } = useDeleteDocument();
  const [showUploadModal, setshowUploadModal] = useState<boolean>(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const handleDeleteDocument = async (documentId: string) => {
    if (confirm('Are you sure you want to delete this document?')) {
      await deleteDocument(documentId);
      await refetch();
      showToastMessage('File has been deleted');
      setShowToast(true);
    }
  };

  const showToastMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  if (error) return <p className="text-red-600">{error}</p>;

  return (
    <div>
      <h3 className="text-lg font-semibold my-4">Supplier Documents</h3>
      <div className="d-flex align-items-center my-3">
        <Button
          size="sm"
          onClick={() => {
            setshowUploadModal(true);
          }}
        >
          + Upload new document
        </Button>
      </div>
      <Row>
        <Col>
          {loading ? (
            <Container fluid className="d-flex vh-100">
              <Row className="justify-content-center align-self-center w-100">
                <Col className="text-center">
                  <Spinner animation="border" role="status" variant="primary" />
                </Col>
              </Row>
            </Container>
          ) : (
            <>
              {docs.length ? (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Uploaded At</th>
                      <th>Download</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {docs.map((doc) => (
                      <tr key={doc.documentId}>
                        <td>{doc.title ?? doc.fileName}</td>
                        <td>
                          {new Date(doc.versionUploadedAt).toLocaleString(
                            undefined,
                            {
                              dateStyle: 'medium',
                              timeStyle: 'short',
                            },
                          )}
                        </td>
                        <td>
                          {doc.signedUrl && (
                            <a
                              href={doc.signedUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary"
                            >
                              Download
                            </a>
                          )}
                        </td>
                        <td>
                          <Button
                            variant="link"
                            className="text-danger p-0"
                            disabled={deleting}
                            onClick={() => {
                              handleDeleteDocument(doc.documentId);
                            }}
                          >
                            <i className="bi bi-x-circle fs-5"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p>No documents found for this supplier</p>
              )}
            </>
          )}
          {deleteError && <p className="text-danger mt-2">{deleteError}</p>}
        </Col>
        <Col>
          <SupplierDocumentUploader
            supplierId={supplierId}
            stackedOverModal={stackedOverModal}
            onUploaded={async () => {
              showToastMessage('File has been uploaded');
              setShowToast(true);
              await refetch();
            }}
            show={showUploadModal}
            onClose={() => {
              setshowUploadModal(false);
            }}
          />
        </Col>
      </Row>

      <ToastContainer position={gToastPosition} className="p-3">
        <Toast
          bg={gToastSentiment}
          onClose={() => setShowToast(false)}
          show={showToast}
          delay={3000}
          autohide
        >
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
}
